import type { StreamRow } from '@testflow/shared';
import { useTranslation } from 'react-i18next';
import { StateChip } from '../../atoms.js';
import { QueryErrorNotice } from '../../query-error-notice.js';

// Task 11 (#98.2): Stream-Tabelle — eine Zeile je Struktur-Knoten (Teilprojekt/Stream)
// mit Fortschritt, offenen Defects/Blockern, E2E-Ampel und Sign-off-Status
// (design-spec.md §3.2). Bewusst NICHT über /reports/summary?node_id gezogen — eigener
// Endpoint (Task 10, loadStreamsBreakdown), eigene Query je Plan wie TrendCard/TestersTableCard.
// Tabellen-Markup-Muster: scope-table.tsx:666-691.
export function StreamsTableCard({
  streams,
  isStreamsLoading,
  isStreamsError,
  onRetryStreams,
}: {
  streams: StreamRow[] | undefined;
  isStreamsLoading: boolean;
  isStreamsError: boolean;
  onRetryStreams: () => void;
}) {
  const { t } = useTranslation();

  return (
    <section className="rounded-card border border-[var(--border)] bg-bg-surface p-4">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-bold text-text-primary">{t('reporting.streams.title')}</h2>
      </div>
      <p className="mb-3 text-[10px] text-text-muted">{t('reporting.scopePlan')}</p>
      {isStreamsError ? (
        <QueryErrorNotice onRetry={onRetryStreams} />
      ) : isStreamsLoading ? (
        <div className="text-sm text-text-muted">{t('common.loading')}</div>
      ) : streams && streams.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-text-muted">
                <th className="px-4 py-2">{t('reporting.streams.name')}</th>
                <th className="px-4 py-2">{t('reporting.streams.progress')}</th>
                <th className="px-4 py-2">{t('reporting.streams.openDefects')}</th>
                <th className="px-4 py-2">{t('reporting.streams.e2e')}</th>
                <th className="px-4 py-2">{t('reporting.streams.signoff')}</th>
              </tr>
            </thead>
            <tbody>
              {streams.map((row) => (
                <tr key={row.node_id} className="border-t border-[var(--border)]">
                  <td className="px-4 py-2 text-text-body">{row.name}</td>
                  <td className="px-4 py-2 tabular-nums text-text-primary">
                    {row.passed}/{row.total}
                  </td>
                  <td className="px-4 py-2 tabular-nums text-text-body">
                    {row.openDefects}
                    {row.openBlocker > 0 ? (
                      <span className="ml-1 text-status-fail">
                        ({row.openBlocker} {t('reporting.streams.blocker')})
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 tabular-nums text-text-body">
                    {row.e2eGreen}/{row.e2eTotal}
                  </td>
                  <td className="px-4 py-2">
                    <StateChip
                      label={
                        row.signoff === 'signed'
                          ? t('reporting.streams.signoffSigned')
                          : row.signoff === 'pending'
                            ? t('reporting.streams.signoffPending')
                            : t('reporting.streams.signoffNone')
                      }
                      tone={
                        row.signoff === 'signed'
                          ? 'ok'
                          : row.signoff === 'pending'
                            ? 'warn'
                            : 'default'
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-sm text-text-muted">{t('reporting.streams.empty')}</div>
      )}
    </section>
  );
}
